import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MapPin, Phone, Mail, Clock, CheckCircle, ArrowRight } from 'lucide-react';

const ContactPage = () => {

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-900/30">
      {/* Hero Section */}
      <section className="bg-[#1B5E20] py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full">
              <span className="text-white font-medium">Get in Touch</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Contact Us
            </h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              We're here to help with all your financial and compliance needs
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-4 gap-6 -mt-8 relative z-10">
              <Card className="text-center hover:shadow-xl transition-all duration-300 border-green-200 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-#2E7D32 to-#1B5E20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MapPin className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-lg text-slate-900">Visit Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    D/6 Civil Township<br />
                    Rourkela, Odisha<br />
                    India - 769004
                  </p>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-all duration-300 border-green-200 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Phone className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-lg text-slate-900">Call Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="tel:+919437046671" className="text-#0D3D13 font-semibold hover:text-green-800 transition-colors">
                    +91 9437046671
                  </a>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-all duration-300 border-green-200 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-lg text-slate-900">Email Us</CardTitle>
                </CardHeader>
                <CardContent>
                  <a href="mailto:SKM.RKL@REDIFFMAIL.COM" className="text-#0D3D13 font-semibold hover:text-green-800 transition-colors break-all text-sm">
                    SKM.RKL@REDIFFMAIL.COM
                  </a>
                </CardContent>
              </Card>

              <Card className="text-center hover:shadow-xl transition-all duration-300 border-green-200 bg-white">
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="h-8 w-8 text-white" />
                  </div>
                  <CardTitle className="text-lg text-slate-900">Business Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 text-sm">
                    Monday - Saturday<br />
                    9:00 AM - 7:00 PM
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Options & Map Section */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Direct Contact Options */}
              <div>
                <div className="mb-8">
                  <h2 className="text-4xl font-bold text-slate-900 mb-4">
                    Schedule a Meeting With Us
                  </h2>
                </div>

                {/* WhatsApp Card */}
                <a 
                  href="https://wa.me/919437046671?text=Hello%20S.K.%20Mundhra%20%26%20Co.%2C%20I%20would%20like%20to%20schedule%20a%20meeting."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block mb-6"
                >
                  <Card className="border-green-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer bg-gradient-to-br from-green-50 to-white">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-slate-900 mb-4">WhatsApp Us</h3>
                          <div className="flex items-center text-[#1B5E20] font-semibold text-lg">
                            <span>Chat with us now</span>
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>

                {/* Email Card */}
                <a 
                  href="mailto:SKM.RKL@REDIFFMAIL.COM?subject=Meeting%20Request&body=Hello%20S.K.%20Mundhra%20%26%20Co.%2C%0A%0AI%20would%20like%20to%20schedule%20a%20meeting%20regarding%3A%0A%0A"
                  className="block mb-6"
                >
                  <Card className="border-purple-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer bg-gradient-to-br from-purple-50 to-white">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <Mail className="w-12 h-12 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-slate-900 mb-4">Mail Us</h3>
                          <div className="flex items-center text-[#1B5E20] font-semibold text-lg">
                            <span>Send email</span>
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>

                {/* Call Card */}
                <a 
                  href="tel:+919437046671"
                  className="block"
                >
                  <Card className="border-blue-200 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] cursor-pointer bg-gradient-to-br from-blue-50 to-white">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-6">
                        <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
                          <Phone className="w-12 h-12 text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold text-slate-900 mb-4">Call Us</h3>
                          <div className="flex items-center text-[#1B5E20] font-semibold text-lg">
                            <span>+91 9437046671</span>
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </a>
              </div>

              {/* Map & Additional Info */}
              <div className="space-y-8">
                <Card className="overflow-hidden border-green-200 shadow-xl">
                  <div className="aspect-[4/3] bg-slate-200">
                    <iframe
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3690.8!2d84.8!3d22.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDE1JzAwLjAiTiA4NMKwNDgnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Office Location"
                    ></iframe>
                  </div>
                  <CardHeader>
                    <CardTitle className="text-xl text-slate-900">Our Office Location</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      Located in the heart of Rourkela, our office is easily accessible and equipped with modern facilities to serve our clients.
                    </p>
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <MapPin className="h-5 w-5 text-#1B5E20 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-slate-900">Address</div>
                          <div className="text-slate-600">D/6 Civil Township, Rourkela, Odisha - 769004</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-slate-900">Phone</div>
                          <a href="tel:+919437046671" className="text-#0D3D13 hover:text-green-800">
                            +91 9437046671
                          </a>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="font-semibold text-slate-900">Email</div>
                          <a href="mailto:SKM.RKL@REDIFFMAIL.COM" className="text-#0D3D13 hover:text-green-800 break-all">
                            SKM.RKL@REDIFFMAIL.COM
                          </a>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-#1B5E20 to-#0D3D13 border-0 text-white shadow-xl">
                  <CardHeader>
                    <CardTitle className="text-2xl">Why Choose Us?</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>35+ years of trusted service</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>Expert team of 20+ professionals</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>Ethical and transparent practice</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 flex-shrink-0" />
                      <span>Comprehensive financial solutions</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
